/* My Account panel (iter-18d).
 *
 * Renders inside Settings → My Account. Surfaces everything a user needs to
 * see about their billing relationship with The Loom:
 *
 *   • Token balances (AI + Copyscape) with quick-top-up button
 *   • Active subscription card (tier, status, next renewal, manage portal)
 *   • Purchase history table (one-time + subscription invoices)
 *   • Per-project usage rollup (where their tokens have been spent)
 *
 * All read-only data flows from existing backend endpoints:
 *   GET  /api/auth/me              (balances + sub fields on the user doc)
 *   GET  /api/subscriptions/me     (canonical sub status — also on the user)
 *   GET  /api/payments/history     (all payment_transactions for the user)
 *   GET  /api/account/usage        (cost_events + project rollup)
 *   POST /api/subscriptions/portal (returns Stripe billing portal URL)
 */
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Coins, ShieldCheck, Crown, ExternalLink, Wallet, Receipt,
  FolderOpen, Loader2, RefreshCw, KeyRound, Mail, Zap, Key,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth_context';
import { paymentsApi, subscriptionsApi, accountApi, authApi } from '@/lib/auth_api';
import { usageApi } from '@/lib/api';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';

const STATUS_BADGE = {
  active:     { label: 'Active',     cls: 'bg-success/15 text-success' },
  trialing:   { label: 'Trialing',   cls: 'bg-primary/15 text-primary' },
  past_due:   { label: 'Past due',   cls: 'bg-warning/20 text-warning' },
  cancelled:  { label: 'Cancelled',  cls: 'bg-muted text-muted-foreground' },
  incomplete: { label: 'Incomplete', cls: 'bg-muted text-muted-foreground' },
};

function fmtCents(amountUsd) {
  if (amountUsd == null) return '—';
  return `$${Number(amountUsd).toFixed(2)}`;
}

function fmtTimestamp(ts) {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return ts; }
}

export function MyAccountPanel({ onBuyTokens }) {
  const { user, refresh } = useAuth();
  const [sub, setSub] = useState(null);
  const [history, setHistory] = useState([]);
  const [usage, setUsage] = useState({ events: [], by_project: [] });
  const [series, setSeries] = useState([]);
  const [llmUsage, setLlmUsage] = useState(null);
  const [seriesDays, setSeriesDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const reload = React.useCallback(async () => {
    setLoading(true);
    try {
      const [s, h, u, sr, lu] = await Promise.all([
        subscriptionsApi.me().catch(() => null),
        paymentsApi.history().catch(() => []),
        accountApi.usage().catch(() => ({ events: [], by_project: [] })),
        accountApi.usageSeries(seriesDays).catch(() => ({ series: [] })),
        usageApi.me().catch(() => null),
      ]);
      setSub(s);
      setHistory(Array.isArray(h) ? h : []);
      setUsage(u || { events: [], by_project: [] });
      setSeries(sr?.series || []);
      setLlmUsage(lu);
    } finally { setLoading(false); }
  }, [seriesDays]);

  useEffect(() => { reload(); refresh?.(); }, [reload, refresh]);

  const openPortal = async () => {
    setBusy(true);
    try {
      const r = await subscriptionsApi.portal(window.location.href);
      window.location.href = r.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not open billing portal.');
      setBusy(false);
    }
  };

  const changeEmail = async () => {
    setBusy(true);
    try {
      await authApi.changeEmail(emailPassword, loginEmail);
      toast.success('Verification sent to your new login email.');
      setLoginEmail(''); setEmailPassword('');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not change login email.');
    } finally { setBusy(false); }
  };

  const changePassword = async () => {
    setBusy(true);
    try {
      const result = await authApi.changePassword(currentPassword, newPassword);
      toast.success(`Password updated. ${result.other_sessions_revoked || 0} other sessions revoked.`);
      setCurrentPassword(''); setNewPassword('');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not change password.');
    } finally { setBusy(false); }
  };

  const tokens = user?.tokens_purchased || 0;
  const csTokens = user?.copyscape_tokens || 0;
  const tier = sub?.tier;
  const status = sub?.status || (tier ? 'active' : null);
  const badge = STATUS_BADGE[status] || { label: status || 'No subscription', cls: 'bg-muted text-muted-foreground' };

  return (
    <div className="space-y-5" data-testid="my-account-panel">
      {/* Balances */}
      <section>
        <h3 className="mb-2 text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">Wallet balances</h3>
        <div className="grid grid-cols-2 gap-3">
          <BalanceCard
            icon={Coins}
            label="AI tokens"
            value={tokens}
            unit="tokens"
            tone="primary"
            testid="balance-ai"
            cta={<Button size="sm" className="h-7 gap-1.5 text-[11px]" onClick={onBuyTokens} data-testid="balance-ai-topup">
              <Wallet className="h-3 w-3" /> Top up
            </Button>}
          />
          <BalanceCard
            icon={ShieldCheck}
            label="Copyscape scans"
            value={csTokens}
            unit="scans"
            tone="success"
            testid="balance-cs"
          />
        </div>
      </section>

      {/* LLM Budget Meter */}
      <LlmBudgetSection llmUsage={llmUsage} loading={loading} onBuyTokens={onBuyTokens} />

      <section data-testid="account-security-section">
        <h3 className="mb-2 text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">Login &amp; security</h3>
        <div className="grid gap-3 lg:grid-cols-2">
          <div className="rounded-md border border-border bg-card p-4">
            <div className="flex items-center gap-2 text-[12px] font-semibold"><Mail className="h-3.5 w-3.5" /> Change login email</div>
            <p className="mt-1 text-[10.5px] text-muted-foreground">Current: {user?.email || '—'}. The new address must be verified.</p>
            <input type="email" value={loginEmail} onChange={(event) => setLoginEmail(event.target.value)} placeholder="New login email" data-testid="account-change-email-input" className="mt-3 h-8 w-full rounded-sm border border-border bg-background px-2 text-[11.5px]" />
            <input type="password" value={emailPassword} onChange={(event) => setEmailPassword(event.target.value)} placeholder="Current password" data-testid="account-change-email-password" className="mt-2 h-8 w-full rounded-sm border border-border bg-background px-2 text-[11.5px]" />
            <Button type="button" size="sm" className="mt-2 h-8" disabled={busy || !loginEmail || !emailPassword} onClick={changeEmail} data-testid="account-change-email-submit">Send verification</Button>
          </div>
          <div className="rounded-md border border-border bg-card p-4">
            <div className="flex items-center gap-2 text-[12px] font-semibold"><KeyRound className="h-3.5 w-3.5" /> Change password</div>
            <p className="mt-1 text-[10.5px] text-muted-foreground">Use at least 10 characters. Other signed-in devices will be logged out.</p>
            <input type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} placeholder="Current password" data-testid="account-current-password-input" className="mt-3 h-8 w-full rounded-sm border border-border bg-background px-2 text-[11.5px]" />
            <input type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} placeholder="New password" data-testid="account-new-password-input" className="mt-2 h-8 w-full rounded-sm border border-border bg-background px-2 text-[11.5px]" />
            <Button type="button" size="sm" className="mt-2 h-8" disabled={busy || !currentPassword || newPassword.length < 10} onClick={changePassword} data-testid="account-change-password-submit">Update password</Button>
          </div>
        </div>
      </section>

      {/* iter-50k — Help-update broadcast opt-in */}
      <HelpUpdatesOptIn />

      {/* Subscription */}
      <section>
        <h3 className="mb-2 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
          <Crown className="h-3 w-3" /> Subscription
        </h3>
        <div className="rounded-md border border-border bg-card p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-[14px] font-semibold capitalize" data-testid="subscription-tier">
                  {tier || 'No active plan'}
                </span>
                <span className={cn('rounded-sm px-1.5 py-0.5 text-[10px] font-medium', badge.cls)}
                       data-testid="subscription-status-badge">
                  {badge.label}
                </span>
                {sub?.cadence && (
                  <span className="rounded-sm bg-secondary px-1.5 py-0.5 text-[10px] capitalize text-muted-foreground">
                    {sub.cadence}
                  </span>
                )}
              </div>
              <div className="mt-1 text-[11.5px] text-muted-foreground">
                {tier
                  ? `Next renewal: ${fmtTimestamp(sub.current_period_end)}`
                  : 'Pick a plan to bundle AI tokens + Copyscape scans with no per-call billing.'}
              </div>
            </div>
            <div className="shrink-0">
              {sub?.has_customer ? (
                <Button size="sm" disabled={busy} onClick={openPortal}
                         data-testid="manage-subscription-btn"
                         className="h-8 gap-1.5">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}
                  Manage subscription
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={onBuyTokens} data-testid="subscribe-cta-btn"
                         className="h-8 gap-1.5">
                  <Crown className="h-3.5 w-3.5" /> View plans
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Purchase history */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
            <Receipt className="h-3 w-3" /> Purchase history
          </h3>
          <button onClick={reload}
                  data-testid="purchase-history-refresh"
                  className="flex items-center gap-1 rounded-sm px-1.5 py-1 text-[10.5px] text-muted-foreground hover:bg-secondary hover:text-foreground">
            <RefreshCw className={cn('h-3 w-3', loading && 'animate-spin')} /> Refresh
          </button>
        </div>
        <div className="rounded-md border border-border bg-card">
          {loading ? (
            <div className="p-6 text-center text-[11.5px] text-muted-foreground">
              <Loader2 className="mx-auto mb-1 h-4 w-4 animate-spin" /> Loading transactions…
            </div>
          ) : history.length === 0 ? (
            <div className="p-6 text-center text-[11.5px] text-muted-foreground" data-testid="purchase-history-table">
              No purchases yet. Top up to see receipts here.
            </div>
          ) : (
            <div className="max-h-72 overflow-auto scroll-thin">
              <table className="w-full text-[11.5px]" data-testid="purchase-history-table">
                <thead className="sticky top-0 bg-panel-header/60 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Date</th>
                    <th className="px-3 py-2 text-left font-medium">Item</th>
                    <th className="px-3 py-2 text-right font-medium">Amount</th>
                    <th className="px-3 py-2 text-right font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((t) => (
                    <tr key={t.session_id} className="border-t border-border" data-testid={`purchase-row-${t.session_id}`}>
                      <td className="px-3 py-2 font-mono-stat text-[10.5px] text-muted-foreground">
                        {fmtTimestamp(t.created_at)}
                      </td>
                      <td className="px-3 py-2">
                        {t.kind === 'subscription_checkout'
                          ? <>Subscription · <b className="capitalize">{t.plan_id}</b> ({t.cadence})</>
                          : t.kind === 'copyscape_pack'
                            ? <>Copyscape pack · <b>{t.tokens} scans</b></>
                            : <>Token pack · <b>{(t.tokens || 0).toLocaleString()} tok</b></>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-stat">{fmtCents(t.amount)}</td>
                      <td className="px-3 py-2 text-right">
                        <span className={cn('rounded-sm px-1.5 py-0.5 text-[10px] font-medium',
                          t.payment_status === 'paid' ? 'bg-success/15 text-success'
                          : t.payment_status === 'unpaid' ? 'bg-warning/20 text-warning'
                          : 'bg-muted text-muted-foreground')}>
                          {t.payment_status || 'pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Project usage breakdown */}
      <section>
        <h3 className="mb-2 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
          <FolderOpen className="h-3 w-3" /> Usage by project
        </h3>
        <div className="rounded-md border border-border bg-card">
          {usage.by_project.length === 0 ? (
            <div className="p-6 text-center text-[11.5px] text-muted-foreground" data-testid="usage-by-project-table">
              No AI activity yet. As you generate chapters or run scans, the per-project rollup appears here.
            </div>
          ) : (
            <table className="w-full text-[11.5px]" data-testid="usage-by-project-table">
              <thead className="bg-panel-header/60 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Project</th>
                  <th className="px-3 py-2 text-right font-medium">Calls</th>
                  <th className="px-3 py-2 text-right font-medium">Tokens spent</th>
                  <th className="px-3 py-2 text-right font-medium">$ spent</th>
                </tr>
              </thead>
              <tbody>
                {usage.by_project.map((p) => (
                  <tr key={p.project_id} className="border-t border-border" data-testid={`usage-project-${p.project_id}`}>
                    <td className="px-3 py-2 truncate max-w-[280px]">{p.project_name || p.project_id}</td>
                    <td className="px-3 py-2 text-right font-mono-stat">{p.events_count}</td>
                    <td className="px-3 py-2 text-right font-mono-stat">{(p.tokens_spent || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono-stat">{fmtCents(p.usd_spent)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Daily activity line chart (iter-21) */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
            Daily activity
          </h3>
          <div className="flex items-center gap-1 rounded-md border border-border bg-panel-header/40 p-0.5 text-[10.5px]">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setSeriesDays(d)}
                data-testid={`usage-range-${d}`}
                className={cn('rounded-sm px-2 py-0.5',
                  seriesDays === d ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary')}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
        <div className="rounded-md border border-border bg-card p-3" data-testid="usage-series-chart">
          {series.length === 0 ? (
            <div className="py-8 text-center text-[11.5px] text-muted-foreground">
              No daily activity in the selected range.
            </div>
          ) : (
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer>
                <LineChart data={series} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                    tickFormatter={(d) => d.slice(5)}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                    tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}
                  />
                  <YAxis
                    yAxisId="right" orientation="right"
                    tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                    tickFormatter={(v) => `$${v.toFixed(2)}`}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      fontSize: 11,
                    }}
                    formatter={(value, name) => {
                      if (name === 'usd') return [`$${Number(value).toFixed(4)}`, '$ spent'];
                      if (name === 'tokens') return [Number(value).toLocaleString(), 'tokens'];
                      return [value, name];
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line yAxisId="left"  dataKey="tokens" name="tokens" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                  <Line yAxisId="right" dataKey="usd"    name="usd"    stroke="hsl(var(--success))" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function BalanceCard({ icon: Icon, label, value, unit, tone = 'primary', cta, testid }) {
  return (
    <div className="rounded-md border border-border bg-card p-3" data-testid={testid}>
      <div className="flex items-start gap-2.5">
        <div className={cn(
          'flex h-7 w-7 items-center justify-center rounded-sm',
          tone === 'success' ? 'bg-success/15 text-success' : 'bg-primary/15 text-primary',
        )}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="flex-1">
          <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{label}</div>
          <div className="mt-0.5 flex items-baseline gap-1">
            <span className="font-mono-stat text-xl font-semibold" data-testid={`${testid}-value`}>
              {Number(value || 0).toLocaleString()}
            </span>
            <span className="text-[10.5px] text-muted-foreground">{unit}</span>
          </div>
        </div>
        {cta}
      </div>
    </div>
  );
}



// iter-50k — opt-in toggle for help-update broadcasts. Backend
// endpoints: GET/POST /api/help/subscribe (cookie auth). Default is OFF.
function HelpUpdatesOptIn() {
  const [optIn, setOptIn] = useState(null); // null=loading
  const [busy, setBusy]   = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    let alive = true;
    fetch(`${BACKEND}/api/help/subscribe`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : { opt_in: false })
      .then((d) => { if (alive) setOptIn(!!d.opt_in); })
      .catch(() => { if (alive) setOptIn(false); });
    return () => { alive = false; };
  }, [BACKEND]);

  const toggle = async () => {
    if (optIn === null) return;
    const next = !optIn;
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND}/api/help/subscribe`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ opt_in: next }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setOptIn(next);
      toast.success(next ? 'Subscribed to help-update broadcasts.' : 'Unsubscribed from help-update broadcasts.');
    } catch (e) {
      toast.error(e?.message || 'Could not update preference.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="help-updates-optin">
      <h3 className="mb-2 text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">Notifications</h3>
      <label className="flex items-start gap-3 rounded-md border border-border bg-card/40 p-3">
        <input
          type="checkbox"
          checked={!!optIn}
          disabled={optIn === null || busy}
          onChange={toggle}
          data-testid="help-updates-checkbox"
          className="mt-1 h-4 w-4 accent-primary"
        />
        <div className="flex-1">
          <div className="text-[12.5px] font-semibold">Email me when the The Loom manual is updated</div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            Occasional broadcasts (typically once per release) describing what changed in the in-app help and how it affects your workflow.
            You can unsubscribe with one click from any email.
          </div>
        </div>
        {busy && <Loader2 className="mt-1 h-3.5 w-3.5 animate-spin text-muted-foreground" />}
      </label>
    </section>
  );
}


/* ── LLM Budget Meter ─────────────────────────────────────────────────────── */
function LlmBudgetSection({ llmUsage, loading, onBuyTokens }) {
  if (loading || !llmUsage) return null;

  const {
    llm_spent_cents = 0,
    llm_cap_cents   = -1,
    is_byok         = false,
    byok_tokens_this_month = 0,
    month = '',
  } = llmUsage;

  const hasCap    = llm_cap_cents > 0;
  const spentUsd  = (llm_spent_cents / 100).toFixed(2);
  const capUsd    = hasCap ? (llm_cap_cents / 100).toFixed(2) : null;
  const pct       = hasCap ? Math.min(100, Math.round((llm_spent_cents / llm_cap_cents) * 100)) : 0;
  const nearLimit = pct >= 80;
  const atLimit   = pct >= 100;

  const barColor  = atLimit  ? 'bg-red-500'
                  : nearLimit ? 'bg-amber-500'
                  : 'bg-emerald-500';

  const [monthLabel] = (() => {
    try {
      const d = new Date(`${month}-01`);
      return [d.toLocaleString(undefined, { month: 'long', year: 'numeric' })];
    } catch { return [month]; }
  })();

  return (
    <section data-testid="llm-budget-section">
      <h3 className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
        <Zap className="h-3 w-3" /> AI Writing Budget — {monthLabel}
      </h3>

      {is_byok ? (
        /* BYOK: show processed tokens, no cap enforcement */
        <div className="rounded-md border border-border bg-card p-4" data-testid="llm-budget-byok">
          <div className="flex items-center gap-2">
            <Key className="h-4 w-4 text-primary/70" />
            <span className="text-[13px] font-semibold">BYOK mode active</span>
          </div>
          <p className="mt-1 text-[11.5px] text-muted-foreground">
            Your own API keys are billed directly to your provider — no platform cap applies.
          </p>
          <div className="mt-3 rounded-sm bg-secondary/40 px-3 py-2 text-[12px]">
            <span className="font-medium">{byok_tokens_this_month.toLocaleString()}</span>
            <span className="ml-1 text-muted-foreground">tokens processed via your keys this month</span>
            {llm_spent_cents > 0 && (
              <span className="ml-2 text-[11px] text-muted-foreground">(≈ ${spentUsd} equivalent)</span>
            )}
          </div>
        </div>
      ) : hasCap ? (
        /* Plan user with a spend cap */
        <div className="rounded-md border border-border bg-card p-4" data-testid="llm-budget-plan">
          <div className="flex items-center justify-between text-[12px]">
            <span>
              <span className={cn('font-semibold', atLimit && 'text-red-500', nearLimit && !atLimit && 'text-amber-600')}>
                ${spentUsd}
              </span>
              <span className="text-muted-foreground"> of ${capUsd} used</span>
            </span>
            <span className={cn(
              'rounded-sm px-1.5 py-0.5 text-[10px] font-medium',
              atLimit  ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
              : nearLimit ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
              : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
            )}>
              {atLimit ? 'Cap reached' : nearLimit ? `${pct}% used` : `${pct}% used`}
            </span>
          </div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-secondary">
            <div
              className={cn('h-full rounded-full transition-all', barColor)}
              style={{ width: `${pct}%` }}
              data-testid="llm-budget-bar"
            />
          </div>
          <p className="mt-1.5 text-[10.5px] text-muted-foreground">
            {atLimit
              ? 'Monthly AI budget reached. Top up tokens or upgrade your plan to continue writing.'
              : nearLimit
              ? `${100 - pct}% of your monthly AI budget remains.`
              : `${100 - pct}% of your monthly AI budget remains.`}
          </p>
          {atLimit && onBuyTokens && (
            <Button size="sm" className="mt-2 h-7 gap-1.5 text-[11px]" onClick={onBuyTokens} data-testid="llm-budget-topup-btn">
              <Wallet className="h-3 w-3" /> Top up tokens
            </Button>
          )}
        </div>
      ) : (
        /* Unlimited plan or starter */
        <div className="rounded-md border border-border bg-card p-4 text-[12px] text-muted-foreground" data-testid="llm-budget-unlimited">
          {llm_spent_cents > 0
            ? <span>≈ <strong className="text-foreground">${spentUsd}</strong> in AI usage this month.</span>
            : <span>No AI usage recorded this month yet.</span>}
          <span className="ml-1">No monthly cap on your current plan.</span>
        </div>
      )}
    </section>
  );
}
